import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { FullscreenHost } from '../fullscreen-host.component';
import styles from './lorem-ipsum-generator.component.module.scss';
import { genStyles } from '../converter-layout/generator-layout';

const WORDS = 'lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua enim ad minim veniam quis nostrud exercitation ullamco laboris nisi aliquip ex ea commodo consequat duis aute irure in reprehenderit voluptate velit esse cillum fugiat nulla pariatur excepteur sint occaecat cupidatat non proident sunt culpa qui officia deserunt mollit anim id est laborum perspiciatis unde omnis iste natus error voluptatem accusantium doloremque laudantium totam rem aperiam eaque ipsa quae ab illo inventore veritatis quasi architecto beatae vitae dicta explicabo nemo ipsam quia voluptas aspernatur aut odit fugit consequuntur magni dolores eos ratione sequi nesciunt neque porro quisquam dolorem adipisci numquam eius modi tempora'.split(' ');

const FIRST_SENTENCE = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.';

interface GenState {
    paragraphs: number;
    wordsPerPara: number;
    startWithLorem: boolean;
    includeHeadings: boolean;
    includeLists: boolean;
    format: 'plain' | 'html' | 'markdown';
    output: string;
    copiedField: string | null;
}

function randomWord(): string { return WORDS[Math.floor(Math.random() * WORDS.length)]; }

function generateSentence(wordCount: number): string {
    const words = Array.from({ length: wordCount }, randomWord);
    words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
    return words.join(' ') + '.';
}

function generateParagraph(targetWords: number): string {
    const sentences: string[] = [];
    let count = 0;
    while (count < targetWords) {
        const len = Math.min(8 + Math.floor(Math.random() * 10), targetWords - count);
        if (len < 3) break;
        sentences.push(generateSentence(len));
        count += len;
    }
    return sentences.join(' ');
}

function generate(state: GenState): string {
    const paras: string[] = [];
    for (let i = 0; i < state.paragraphs; i++) {
        let p = generateParagraph(state.wordsPerPara);
        if (i === 0 && state.startWithLorem) p = FIRST_SENTENCE + ' ' + p;
        paras.push(p);
    }

    if (state.format === 'html') {
        const parts: string[] = [];
        paras.forEach((p, i) => {
            if (state.includeHeadings && i > 0 && i % 2 === 0) parts.push(`<h2>${generateSentence(4).slice(0, -1)}</h2>`);
            parts.push(`<p>${p}</p>`);
            if (state.includeLists && i % 3 === 1) {
                parts.push('<ul>');
                for (let j = 0; j < 3; j++) parts.push(`  <li>${generateSentence(6)}</li>`);
                parts.push('</ul>');
            }
        });
        return parts.join('\n');
    }

    if (state.format === 'markdown') {
        const parts: string[] = [];
        paras.forEach((p, i) => {
            if (state.includeHeadings && i > 0 && i % 2 === 0) parts.push(`## ${generateSentence(4).slice(0, -1)}\n`);
            parts.push(p);
            if (state.includeLists && i % 3 === 1) {
                parts.push('');
                for (let j = 0; j < 3; j++) parts.push(`- ${generateSentence(6)}`);
            }
            parts.push('');
        });
        return parts.join('\n').trim();
    }

    return paras.join('\n\n');
}

export default class LoremIpsumGeneratorComponent extends Component<{}, GenState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(props) {
        super(props);
        this.state = {
            paragraphs: 3, wordsPerPara: 50, startWithLorem: true,
            includeHeadings: false, includeLists: false, format: 'plain',
            output: '', copiedField: null,
        };
    }

    componentDidMount() {
        this.setState(s => ({ output: generate(s) }));
    }

    regenerate = () => this.setState(s => ({ output: generate(s), copiedField: null }));

    updateAndRegenerate = (patch: Partial<GenState>) => {
        this.setState(s => {
            const next = { ...s, ...patch };
            return { ...next, output: generate(next as GenState), copiedField: null };
        });
    };

    copyText = () => {
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedField: 'text' });
            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
        });
    };

    render() {
        const { paragraphs, wordsPerPara, startWithLorem, includeHeadings, includeLists, format, output, copiedField } = this.state;
        const wordCount = output.trim().split(/\s+/).length;
        const charCount = output.length;
        const isCopied = copiedField === 'text';
        const hasPreview = format === 'html' || format === 'markdown';

        const previewHtml = format === 'html' ? output
            : format === 'markdown' ? output.split('\n').map(line => {
                if (line.startsWith('## ')) return `<h2>${line.slice(3)}</h2>`;
                if (line.startsWith('- ')) return `<li>${line.slice(2)}</li>`;
                if (line.trim() === '') return '';
                return `<p>${line}</p>`;
            }).join('\n') : '';

        return (
            <FullscreenHost>
            <div className="row">
                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={styles.controls}>
                            <div className={styles.sliderGroup}>
                                <label className={sharedStyles.label}>Paragraphs: <strong>{paragraphs}</strong></label>
                                <input type="range" min={1} max={20} value={paragraphs}
                                    onChange={e => this.updateAndRegenerate({ paragraphs: +e.target.value })}
                                    className={styles.slider} />
                            </div>
                            <div className={styles.sliderGroup}>
                                <label className={sharedStyles.label}>Words/paragraph: <strong>{wordsPerPara}</strong></label>
                                <input type="range" min={20} max={200} step={10} value={wordsPerPara}
                                    onChange={e => this.updateAndRegenerate({ wordsPerPara: +e.target.value })}
                                    className={styles.slider} />
                            </div>
                        </div>

                        <div className={styles.options}>
                            <label className={styles.checkbox}>
                                <input type="checkbox" checked={startWithLorem}
                                    onChange={e => this.updateAndRegenerate({ startWithLorem: e.target.checked })} />
                                Start with "Lorem ipsum..."
                            </label>
                            <label className={styles.checkbox}>
                                <input type="checkbox" checked={includeHeadings}
                                    onChange={e => this.updateAndRegenerate({ includeHeadings: e.target.checked })} />
                                Include headings
                            </label>
                            <label className={styles.checkbox}>
                                <input type="checkbox" checked={includeLists}
                                    onChange={e => this.updateAndRegenerate({ includeLists: e.target.checked })} />
                                Include bullet lists
                            </label>
                        </div>

                        <div className={styles.formatToggle}>
                            {(['plain', 'html', 'markdown'] as const).map(f => (
                                <button key={f} className={`${styles.formatButton} ${format === f ? styles.formatActive : ''}`}
                                    onClick={() => this.updateAndRegenerate({ format: f })}>
                                    {f === 'plain' ? 'Plain Text' : f === 'html' ? 'HTML' : 'Markdown'}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="col-12">
                    <div className={genStyles.section}>
                        <textarea className={sharedStyles.textarea} value={output} readOnly rows={12} />
                    </div>
                </div>

                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={sharedStyles.stats}>
                            <span>{paragraphs} paragraphs · {wordCount} words · {charCount} characters</span>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button className={sharedStyles.copyButton} onClick={this.regenerate}>🔄 Regenerate</button>
                                <button className={`${sharedStyles.copyButton} ${isCopied ? sharedStyles.copyButtonSuccess : ''}`}
                                    onClick={this.copyText}>
                                    {isCopied ? '✓ Copied' : 'Copy Text'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {hasPreview && (
                    <div className="col-12">
                        <div className={genStyles.section}>
                            <label className={sharedStyles.label} style={{ marginTop: '1rem', display: 'block' }}>Preview</label>
                            <div className={styles.previewPane} dangerouslySetInnerHTML={{ __html: previewHtml }} />
                        </div>
                    </div>
                )}
            </div>

            </FullscreenHost>
        );
    }
}

